// react_app/src/components/admin/translations/TranslationsTab.tsx

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAlert } from '../../../contexts/AlertContext';
import { translationApi, type LanguageDto, type LanguageTextDto } from '../../../api/translationService';
import { FeatureGroupTree } from './FeatureGroupTree';
import { TranslationToolbar } from './TranslationToolbar';
import { TranslationTable } from './TranslationTable';
import { ImportPreviewDialog } from './ImportPreviewDialog';

const PAGE_SIZE = 50;

interface TranslationsTabProps {
  languages: LanguageDto[];
}

export function TranslationsTab({ languages }: TranslationsTabProps) {
  const { t } = useTranslation();
  const { showToast } = useAlert();

  const targetLanguages = languages.filter((l) => l.isEnabled && l.cultureName !== 'en');
  const [selectedLanguage, setSelectedLanguage] = useState(targetLanguages[0]?.cultureName || '');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [untranslatedOnly, setUntranslatedOnly] = useState(false);
  const [page, setPage] = useState(1);

  // All texts for the sidebar counts
  const [allTexts, setAllTexts] = useState<LanguageTextDto[]>([]);
  const [allTextsLoading, setAllTextsLoading] = useState(false);

  // Paginated texts for the table
  const [pageTexts, setPageTexts] = useState<LanguageTextDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Import
  const [importCsv, setImportCsv] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load all texts for sidebar (once per language switch)
  useEffect(() => {
    if (!selectedLanguage) return;
    let cancelled = false;
    setAllTextsLoading(true);
    translationApi.getAllLanguageTexts(selectedLanguage).then((data) => {
      if (!cancelled) {
        setAllTexts(data);
        setAllTextsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setAllTextsLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedLanguage]);

  // Load paginated texts for table
  const loadPage = useCallback(async () => {
    if (!selectedLanguage) return;
    setLoading(true);
    try {
      const filterParts: string[] = [];
      if (searchQuery) filterParts.push(searchQuery);
      if (selectedGroup) filterParts.push(selectedGroup);
      const filter = filterParts.join(' ') || undefined;

      const result = await translationApi.getLanguageTexts({
        targetCultureName: selectedLanguage,
        filter,
        getOnlyEmptyValues: untranslatedOnly || undefined,
        skipCount: (page - 1) * PAGE_SIZE,
        maxResultCount: PAGE_SIZE,
      });
      setPageTexts(result.items);
      setTotalCount(result.totalCount);
    } catch {
      setPageTexts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage, selectedGroup, searchQuery, untranslatedOnly, page]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedLanguage, selectedGroup, searchQuery, untranslatedOnly]);

  const handleTextUpdated = (name: string, newValue: string) => {
    setAllTexts((prev) =>
      prev.map((tx) => (tx.name === name ? { ...tx, value: newValue } : tx))
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = await translationApi.getAllLanguageTexts(selectedLanguage);
      const lines = ['Key,Base (en),Translation'];
      for (const tx of all) {
        const escape = (s: string) => `"${(s || '').replace(/"/g, '""')}"`;
        lines.push(`${escape(tx.name)},${escape(tx.baseValue)},${escape(tx.value)}`);
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations-${selectedLanguage}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast(t('App.Common.Error'), { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImportCsv(reader.result as string);
    };
    reader.onerror = () => {
      showToast(t('App.Admin.Translations.Import.ParseError'), { type: 'error' });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportComplete = () => {
    setImportCsv(null);
    showToast(t('App.Admin.Translations.Import.Success', { count: 0, newCount: 0, updatedCount: 0 }), { type: 'success' });
    // Reload data
    loadPage();
    if (selectedLanguage) {
      translationApi.getAllLanguageTexts(selectedLanguage).then(setAllTexts);
    }
  };

  if (targetLanguages.length === 0) {
    return (
      <div className="text-center py-20 text-neutral-500">
        {t('App.Admin.Translations.NoLanguages')}
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div>
      <TranslationToolbar
        languages={languages}
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        untranslatedOnly={untranslatedOnly}
        onUntranslatedOnlyChange={setUntranslatedOnly}
        onExport={handleExport}
        onImport={handleImportClick}
        exporting={exporting}
      />

      <div className="flex gap-6">
        {/* Left sidebar */}
        <div className="w-64 flex-shrink-0 hidden lg:block border-r border-neutral-200 pr-4 max-h-[70vh] overflow-y-auto">
          <FeatureGroupTree
            texts={allTexts}
            selectedGroup={selectedGroup}
            onSelectGroup={(g) => setSelectedGroup(g)}
          />
        </div>

        {/* Mobile group selector */}
        <select
          value={selectedGroup || ''}
          onChange={(e) => setSelectedGroup(e.target.value || null)}
          className="lg:hidden w-full mb-4 px-3 py-2 border border-neutral-200 rounded-md text-sm min-h-[44px]"
        >
          <option value="">{t('App.Admin.Translations.AllGroups')}</option>
          {/* Groups derived from allTexts */}
        </select>

        {/* Right panel */}
        <div className="flex-1 min-w-0">
          <TranslationTable
            texts={pageTexts}
            cultureName={selectedLanguage}
            loading={loading || allTextsLoading}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onTextUpdated={handleTextUpdated}
          />
        </div>
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelected}
        className="hidden"
      />

      {/* Import dialog */}
      {importCsv && (
        <ImportPreviewDialog
          cultureName={selectedLanguage}
          csvContent={importCsv}
          existingTexts={allTexts}
          onClose={() => setImportCsv(null)}
          onComplete={handleImportComplete}
        />
      )}
    </div>
  );
}
